import {
  type HighlightRect,
  Outline,
  OutlineChildItems,
  OutlineItem,
  Root,
  Search,
  Thumbnail,
  Thumbnails,
} from '@anaralabs/lector';
import { atom, useAtom } from 'jotai';
import { LayoutList, SearchIcon } from 'lucide-react';
import { useTheme } from 'next-themes';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import * as pdfjs from 'pdfjs-dist';
import 'pdfjs-dist/web/pdf_viewer.css';
import { cn } from '@/utils/utils';
import { AnimatePresence } from 'motion/react';
import { useCallback, useMemo, useState } from 'react';
import { match } from 'ts-pattern';
import { Button } from '../Button/Button';
import { Icon } from '../ui/Icon';
import { ToggleGroup, ToggleGroupItem } from '../ui/ToggleGroup';
import { HighlightManager } from './HighlightManager';
import { PDFViewerSidebar } from './PDFViewerSidebar';
import { SearchMenu } from './SearchMenu';
import ZoomMenu from './ZoomMenu';

if (typeof Promise.withResolvers === 'undefined') {
  const polyfilledResolvers = () => {
    // biome-ignore lint/suspicious/noExplicitAny: Need any type for this polyfill
    let resolvePromise: any = undefined;
    // biome-ignore lint/suspicious/noExplicitAny: Need any type for this polyfill
    let rejectPromise: any = undefined;

    const promise = new Promise((resolve, reject) => {
      resolvePromise = resolve;
      rejectPromise = reject;
    });

    return {
      promise: promise,
      resolve: resolvePromise,
      reject: rejectPromise,
    };
  };
  if (window) {
    // @ts-ignore: TypeScript doesn't know about this
    window.Promise.withResolvers = polyfilledResolvers;
  } else {
    // @ts-ignore: TypeScript doesn't know about this
    global.Promise.withResolvers = polyfilledResolvers;
  }
}

if (typeof window !== 'undefined') {
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
    import.meta.url,
  ).toString();
}

import { api } from '@/lib/trpc/react';
import DocumentMenu from './DocumentMenu';
import { PageNavigation } from './PageNavigation';
import { PdfContent } from './pdf-viewer/pdf-content';

interface PDFViewerProps {
  documentUrl: string;
  chatbotId: string;
}

const CustomSpinner = () => (
  <div className="flex justify-center items-center h-full w-full ">
    <div className="loader-gray" />
  </div>
);

export const highlightAtom = atom<
  | null
  | {
      type: 'page';
      pageNumber: number;
    }
  | { type: 'chunk'; rects: HighlightRect[]; rectType: 'percent' | 'pixels' }
>(null);

export const useHighlightArea = () => {
  const [_, setHighlight] = useAtom(highlightAtom);

  const jumpToPage = (pageNumber: number) => {
    setHighlight({ type: 'page', pageNumber });
  };

  const jumpToHighlightArea = (
    rects: HighlightRect[],
    rectType?: 'percent' | 'pixels',
  ) => {
    setHighlight({ type: 'chunk', rects, rectType: rectType || 'percent' });
  };

  return { jumpToPage, jumpToHighlightArea };
};

export type PDFViewerSidebarType = 'thumbnails' | 'search' | 'outline';

const PDFViewer = ({ chatbotId, documentUrl }: PDFViewerProps) => {
  const [hasOutline, setHasOutline] = useState(false);
  const [currentLoadedUrl, setCurrentLoadedUrl] = useState<string | null>(null);
  const { resolvedTheme } = useTheme();
  const { data, isLoading } = api.documentAnnotation.all.useQuery(chatbotId);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sidebar, setSidebar] = useState<PDFViewerSidebarType>('thumbnails');

  const onDocumentLoad = useCallback(
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    async ({ proxy, source }: { proxy: PDFDocumentProxy; source: any }) => {
      const outline = await proxy.getOutline();
      setCurrentLoadedUrl(source as string);
      if (outline?.length > 0) setHasOutline(true);
    },
    [],
  );

  const zoomOptions = useMemo(
    () => ({
      maxZoom: 5,
      minZoom: 0.5,
    }),
    [],
  );

  return (
    <Root
      isZoomFitWidth={true}
      onDocumentLoad={onDocumentLoad}
      zoomOptions={zoomOptions}
      source={documentUrl}
      className="bg-background overflow-hidden relative h-full"
      loader={<CustomSpinner />}
    >
      <HighlightManager
        documentUrl={documentUrl}
        currentLoadedUrl={currentLoadedUrl}
      />
      <div className="flex flex-col h-full">
        <div className="px-2 py-1.5 bg-background border-b border-border flex flex-row items-center relative justify-between">
          <div className="flex flex-row gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="text-textGray4"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            >
              <Icon as={LayoutList} />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="text-textGray4"
              onClick={() => {
                setIsSidebarOpen(true);
                setSidebar('search');
              }}
            >
              <Icon as={SearchIcon} />
            </Button>

            <ZoomMenu />
          </div>
          <PageNavigation />

          <DocumentMenu documentUrl={documentUrl} />
        </div>

        <div className="flex flex-grow overflow-hidden relative">
          <AnimatePresence>
            {isSidebarOpen && (
              <PDFViewerSidebar>
                <div className="py-3">
                  <ToggleGroup
                    defaultValue="thumbnails"
                    type="single"
                    value={sidebar}
                    className="px-3 justify-start"
                    onValueChange={(val) =>
                      setSidebar(val as PDFViewerSidebarType)
                    }
                  >
                    <ToggleGroupItem size="sm" value="thumbnails">
                      Thumbnails
                    </ToggleGroupItem>
                    <ToggleGroupItem
                      disabled={!hasOutline}
                      size="sm"
                      value="outline"
                    >
                      Outline
                    </ToggleGroupItem>
                    <ToggleGroupItem size="sm" value="search">
                      Search
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>
                <div className="h-full overflow-auto pb-16">
                  <div className="overflow-y-auto h-full overflow-x-hidden">
                    <div className="w-full h-full overflow-x-hidden px-3">
                      <Search loading={null}>
                        {match(sidebar)
                          .with('thumbnails', () => (
                            <Thumbnails className="flex flex-col items-center ">
                              <Thumbnail
                                className={cn(
                                  'border-secondary border-4 outline-blue outline-4 rounded-md m-1 transition-all duration-200 hover:!border-border cursor-pointer shadow-none relative w-48',
                                  resolvedTheme === 'dark' &&
                                    'invert-[91%] hue-rotate-180 brightness-[80%] contrast-[228%] bg-background !border-muted-foreground',
                                )}
                              />
                            </Thumbnails>
                          ))
                          .with('outline', () => (
                            <Outline className="pl-2">
                              <OutlineItem className="text-sm [&>a]:py-1 [&>a]:block [&>a:hover]:underline">
                                <OutlineChildItems className="pl-4" />
                              </OutlineItem>
                            </Outline>
                          ))
                          .with('search', () => <SearchMenu />)
                          .otherwise(() => null)}
                      </Search>
                    </div>
                  </div>
                </div>
              </PDFViewerSidebar>
            )}
          </AnimatePresence>
          {!isLoading && data?.annotations && (
            <PdfContent
              chatbotId={chatbotId}
              defaultAnnotations={data?.annotations}
            />
          )}
        </div>
      </div>
    </Root>
  );
};

export default PDFViewer;
